/**
 * useJournifiSync Hook
 * 
 * React hook for real-time JournifiSync integration
 * Automatically imports trades as they happen in NinjaTrader
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { startWatching, stopWatching } from '../services/journifiSyncWatcher';
import { futuresSpecs, safeTicks, getSpec } from '../config/futuresSpecs';
import {
  processExecutionsToTrades,
  buildTradeSignature,
  getTradingDay,
  filterAlreadyJournaledExecutions,
  dedupExecutionsByExecutionId,
} from '../services/tradeProcessor';
import { formatEtTime, getCurrentTradingDay } from '../services/timeService';

// Default sync folder path - ORIGINAL WORKING PATH
const DEFAULT_SYNC_FOLDER = 'C:\\NinjaTrader8Data\\JournifiSync';

// LocalStorage keys
const STORAGE_KEY_AUTO_START = 'journifiSync_autoStart';
const STORAGE_KEY_SYNC_FOLDER = 'journifiSync_syncFolder';
const STORAGE_KEY_SOUND_ENABLED = 'journifiSync_soundEnabled';
const STORAGE_KEY_ACCOUNT_MAPPINGS = 'journifiSync_accountMappings';
const STORAGE_KEY_CONFIG_VERSION = 'journifiSync_configVersion';

// Current config version - increment when sync folder path changes
// v2 -> v3: Reverted to original C:\NinjaTrader8Data path (ProgramData had permission issues)
const CURRENT_CONFIG_VERSION = 3;

/**
 * Migration: Ensure sync folder path is updated for users upgrading from older versions
 * This runs once on load and clears any legacy path settings
 */
const runMigrations = () => {
  try {
    const storedVersion = parseInt(localStorage.getItem(STORAGE_KEY_CONFIG_VERSION) || '0', 10);
    
    if (storedVersion < CURRENT_CONFIG_VERSION) {
      console.log(`[JournifiSync] Running migration from config v${storedVersion} to v${CURRENT_CONFIG_VERSION}`);
      
      // Clear old sync folder path - will reset to new default
      const oldPath = localStorage.getItem(STORAGE_KEY_SYNC_FOLDER);
      if (oldPath && oldPath !== DEFAULT_SYNC_FOLDER) {
        console.log(`[JournifiSync] Clearing legacy sync folder path: ${oldPath}`);
        localStorage.removeItem(STORAGE_KEY_SYNC_FOLDER);
      }
      
      // Mark migration complete
      localStorage.setItem(STORAGE_KEY_CONFIG_VERSION, CURRENT_CONFIG_VERSION.toString());
      console.log('[JournifiSync] Migration complete - using default path:', DEFAULT_SYNC_FOLDER);
    }
  } catch (e) {
    console.warn('[JournifiSync] Migration check failed:', e);
  }
};

// Run migrations on module load
runMigrations();

/**
 * Get auto-start preference from localStorage
 */
const getAutoStartPreference = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_AUTO_START) === 'true';
  } catch {
    return false;
  }
};

/**
 * Save auto-start preference to localStorage
 */
const saveAutoStartPreference = (enabled) => {
  try {
    localStorage.setItem(STORAGE_KEY_AUTO_START, enabled ? 'true' : 'false');
  } catch (e) {
    console.warn('Failed to save auto-start preference:', e);
  }
};

/**
 * Get sync folder path from localStorage
 */
const getSyncFolderPreference = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_SYNC_FOLDER) || DEFAULT_SYNC_FOLDER;
  } catch {
    return DEFAULT_SYNC_FOLDER;
  }
};

/**
 * Save sync folder path to localStorage
 */
const saveSyncFolderPreference = (path) => {
  try {
    localStorage.setItem(STORAGE_KEY_SYNC_FOLDER, path);
  } catch (e) {
    console.warn('Failed to save sync folder preference:', e);
  }
};

/**
 * Get sound enabled preference from localStorage
 */
const getSoundEnabledPreference = () => {
  try {
    // Default to true for new users
    const stored = localStorage.getItem(STORAGE_KEY_SOUND_ENABLED);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
};

/**
 * Save sound enabled preference to localStorage
 */
const saveSoundEnabledPreference = (enabled) => {
  try {
    localStorage.setItem(STORAGE_KEY_SOUND_ENABLED, enabled ? 'true' : 'false');
  } catch (e) {
    console.warn('Failed to save sound preference:', e);
  }
};

/**
 * Get account mappings from localStorage
 * Returns object like { "NT_Account_Name": "journifi_account_id", ... }
 */
const getAccountMappings = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ACCOUNT_MAPPINGS);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Save account mappings to localStorage
 */
const saveAccountMappings = (mappings) => {
  try {
    localStorage.setItem(STORAGE_KEY_ACCOUNT_MAPPINGS, JSON.stringify(mappings));
  } catch (e) {
    console.warn('Failed to save account mappings:', e);
  }
};

/**
 * Play notification sound
 */
const playNotificationSound = (isWin = true) => {
  try {
    // Use Web Audio API for a simple notification sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Different sounds for win/loss
    if (isWin) {
      // Pleasant two-tone chime for wins
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // C#6
    } else {
      // Lower single tone for losses
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4
    }
    
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    console.warn('Failed to play notification sound:', e);
  }
};

/**
 * Check if running in Electron environment
 */
const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI?.isElectron;
};

// Trade processing functions (parseAtmTemplateName, processExecutionsToTrades, buildJournalTrade)
// are imported from '../services/tradeProcessor.js' — single source of truth.

/**
 * useJournifiSync Hook
 * 
 * @param {Object} options
 * @param {Array} options.accounts - Existing accounts for matching
 * @param {Array} options.existingTrades - Existing journal trades for proper deduplication
 * @param {Function} options.onTradeComplete - Callback when a new trade is completed
 * @param {Function} options.onTradeUpdate - Callback when an existing open trade receives exits
 * @param {Function} options.onPositionUpdate - Callback for position updates
 * @param {Function} options.onNewAccountDetected - Callback when a new NT account with no mapping/match is seen; returns the auto-created journal account ID (or undefined to let the hook just flag it)
 * @param {boolean} options.autoStart - Start watching automatically
 */
const useJournifiSync = (options = {}) => {
  const {
    accounts = [],
    existingTrades = [],  // Add existing trades to check what's actually imported
    initialAccountMappings,  // Persisted mappings from settings.json (source of truth on load)
    onTradeComplete,
    onTradeUpdate,  // NEW: Called when exits arrive for an existing open trade
    onPositionUpdate,
    onNewAccountDetected,  // NEW: Auto-create a journal account for an unknown NT account
    autoStart = false
  } = options;

  const [isActive, setIsActive] = useState(false);
  const [status, setStatus] = useState({ watching: false, error: null });
  const [pendingExecutions, setPendingExecutions] = useState({}); // Group by account+symbol
  const [recentTrades, setRecentTrades] = useState([]);
  const [syncLog, setSyncLog] = useState([]); // Activity log
  const [autoStartEnabled, setAutoStartEnabledState] = useState(getAutoStartPreference);
  const [lastSyncTime, setLastSyncTime] = useState(null); // Timestamp of last completed trade
  const [syncFolder, setSyncFolderState] = useState(getSyncFolderPreference);
  const [soundEnabled, setSoundEnabledState] = useState(getSoundEnabledPreference);
  // Prefer persisted mappings from settings.json; fall back to localStorage for migration
  const [accountMappings, setAccountMappingsState] = useState(
    () => (initialAccountMappings && Object.keys(initialAccountMappings).length > 0)
      ? initialAccountMappings
      : getAccountMappings()
  );
  const [ninjatraderConnected, setNinjatraderConnected] = useState(false); // 💓 NT connection status
  const [unmappedAccounts, setUnmappedAccounts] = useState([]); // NT account names seen in executions that don't match any journal account
  
  // Wrapper to save account mappings when changed
  // Value of '' means "registered but auto-detect"; null/undefined removes the key entirely
  const setAccountMapping = useCallback((ntAccountName, journifiAccountId) => {
    setAccountMappingsState(prev => {
      const updated = { ...prev };
      if (journifiAccountId === null || journifiAccountId === undefined) {
        delete updated[ntAccountName]; // Only remove on explicit null/undefined
      } else {
        updated[ntAccountName] = journifiAccountId; // '' = auto-detect, 'id' = explicit
      }
      saveAccountMappings(updated);
      return updated;
    });
    // Clear from unmapped list once a real mapping is set (empty string = still unresolved)
    if (journifiAccountId) {
      setUnmappedAccounts(prev => prev.filter(n => n !== ntAccountName));
    }
  }, []);

  // Dismiss an unmapped account warning without creating a mapping
  const dismissUnmappedAccount = useCallback((ntAccountName) => {
    setUnmappedAccounts(prev => prev.filter(n => n !== ntAccountName));
  }, []);
  
  // Clear all account mappings
  const clearAccountMappings = useCallback(() => {
    setAccountMappingsState({});
    saveAccountMappings({});
  }, []);
  
  // Wrapper to save sound preference when changed
  const setSoundEnabled = useCallback((enabled) => {
    setSoundEnabledState(enabled);
    saveSoundEnabledPreference(enabled);
  }, []);
  
  // Wrapper to save sync folder when changed
  const setSyncFolder = useCallback((path) => {
    const trimmedPath = path.trim();
    setSyncFolderState(trimmedPath);
    saveSyncFolderPreference(trimmedPath);
  }, []);
  
  // Wrapper to save auto-start preference when changed
  const setAutoStartEnabled = useCallback((enabled) => {
    setAutoStartEnabledState(enabled);
    saveAutoStartPreference(enabled);
  }, []);
  
  // Helper to add log entries
  const addLogEntry = useCallback((type, message) => {
    setSyncLog(prev => [{
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      type, // 'info' | 'success' | 'error' | 'warning'
      message
    }, ...prev].slice(0, 50)); // Keep last 50 entries
  }, []);
  
  // Refs to avoid stale closures
  const accountsRef = useRef(accounts);
  const onTradeCompleteRef = useRef(onTradeComplete);
  const onTradeUpdateRef = useRef(onTradeUpdate);
  const onPositionUpdateRef = useRef(onPositionUpdate);
  const soundEnabledRef = useRef(soundEnabled);
  const accountMappingsRef = useRef(accountMappings);
  // Mirror of pendingExecutions for Strict-Mode-safe reads inside handlers.
  // (We mutate state via the function-body path, never via a setState updater
  // that has side effects — Strict Mode would invoke the updater twice and
  // double-emit trades / mutate refs / push to outer arrays.)
  const pendingExecutionsRef = useRef({});

  // Track open trades already sent to journal: Map<"ntAccount|symbol", { tradeId, execIds: Set<string> }>
  // Prevents duplicate onTradeComplete calls and routes completions through onTradeUpdate
  // The execIds allow us to verify that a "completed" trade actually belongs to this open position
  // (prevents collision when multiple sequential trades for same symbol exist in pendingExecutions)
  const openTradeMapRef = useRef(new Map());

  // Track NT accounts we've already tried to auto-create this session (prevents spam)
  const autoCreatedRef = useRef(new Set());
  // Track which (ntName, mapping) pairs we've already warned about pointing to non-Active journal accounts
  const inactiveMappingWarnedRef = useRef(new Set());

  // Update refs when props/state change
  useEffect(() => {
    accountsRef.current = accounts;
    onTradeCompleteRef.current = onTradeComplete;
    onTradeUpdateRef.current = onTradeUpdate;
    onPositionUpdateRef.current = onPositionUpdate;
    soundEnabledRef.current = soundEnabled;
    accountMappingsRef.current = accountMappings;
  }, [accounts, onTradeComplete, onTradeUpdate, onPositionUpdate, soundEnabled, accountMappings]);

  // Keep pendingExecutionsRef in sync — read from this inside handlers, never
  // from the React state directly (which would be stale across the same tick).
  useEffect(() => {
    pendingExecutionsRef.current = pendingExecutions;
  }, [pendingExecutions]);

  /**
   * Try to complete open journal trades with new exit executions.
   *
   * Scenario: A trade entry was imported (manually or real-time) as isOpen:true.
   * Later, the exit executions arrive. Without this function, those exits are
   * orphaned because the entry already lives in the journal, not in pendingExecutions.
   *
   * This function finds matching open trades in the journal, combines their entry
   * execution IDs with the new exit executions, reprocesses as a complete trade,
   * and calls onTradeUpdate to update the journal entry in-place.
   */
  const tryCompleteOpenTrades = useCallback((orphanedExecs) => {
    if (!orphanedExecs || orphanedExecs.length === 0) return;
    if (!onTradeUpdateRef.current) {
      console.log('[useJournifiSync] No onTradeUpdate callback — cannot complete open trades');
      return;
    }

    const existingTrades = existingTradesRef.current || [];
    const openTrades = existingTrades.filter(t => t.isOpen && t._journifiSync);

    if (openTrades.length === 0) return;

    // Build a set of ALL execution IDs already in the journal (open + closed)
    // to prevent double-counting exits that were already applied
    const journalExecIds = new Set();
    existingTrades.forEach(t => {
      if (t._journifiSync?.executionIds) {
        t._journifiSync.executionIds.forEach(id => journalExecIds.add(id));
      }
    });

    // Group orphaned executions by account + symbol
    const orphanGroups = {};
    orphanedExecs.forEach(exec => {
      // Skip executions already in the journal
      if (journalExecIds.has(exec.ExecutionId)) return;

      const symbol = exec.InstrumentType || (exec.InstrumentName || exec.Instrument || '').replace(/[HMUZ]\d{1,2}$/, '');
      const accountName = exec.AccountName || 'unknown';
      const key = `${accountName}|${symbol}`;
      if (!orphanGroups[key]) orphanGroups[key] = [];
      orphanGroups[key].push(exec);
    });

    // Track which open trades we've already matched to prevent double-matching
    const matchedTradeIds = new Set();

    Object.entries(orphanGroups).forEach(([key, execs]) => {
      const [accountName, symbol] = key.split('|');

      // Resolve this account's mapped ID for matching
      const mappedAccountId = accountMappingsRef.current?.[accountName] || '';

      // Derive the trading day(s) represented by this orphan group.
      // Exits should only complete trades opened on the SAME trading day —
      // otherwise yesterday's still-open trade can absorb today's exits.
      const groupTradingDays = new Set(
        execs.map(e => getTradingDay(e.Timestamp)).filter(Boolean)
      );

      // Find open trades matching this account + symbol AND opened on a
      // trading day represented in this orphan group (STRICT account matching).
      const symbolMatches = openTrades.filter(t => {
        if (matchedTradeIds.has(t.id)) return false; // Already matched
        if ((t.symbol || '') !== symbol) return false;
        // Date guard: trade.date is YYYY-MM-DD trading day; if missing, allow
        // (legacy trades without date can still fall through, same as before)
        if (t.date && groupTradingDays.size > 0 && !groupTradingDays.has(t.date)) {
          return false;
        }
        return true;
      });

      if (symbolMatches.length === 0) return;

      // Try strict account match first
      let matchedTrade = null;
      if (mappedAccountId) {
        matchedTrade = symbolMatches.find(t => t.accountId === mappedAccountId);
      }

      // Fallback: ONLY if there's exactly one open trade for this symbol on
      // this trading day (unambiguous). Cross-day fallback is FORBIDDEN —
      // symbolMatches has already been date-filtered above, so this is safe.
      if (!matchedTrade && symbolMatches.length === 1) {
        console.log(`[useJournifiSync] No account mapping for "${accountName}" — using sole open ${symbol} trade as match`);
        matchedTrade = symbolMatches[0];
      }

      if (!matchedTrade) {
        console.log(`[useJournifiSync] Could not match orphaned exits for ${accountName}|${symbol} — ${symbolMatches.length} candidates, need account mapping`);
        if (symbolMatches.length > 1) {
          addLogEntry('warning', `Cannot match exits for "${accountName}" ${symbol} — ${symbolMatches.length} open trades found. Set up Account Mapping to resolve.`);
        }
        return;
      }

      // Mark this trade as matched so it can't be matched again by another group
      matchedTradeIds.add(matchedTrade.id);

      const openTrade = matchedTrade;
      const direction = openTrade.direction;

      // Filter to only ACTUAL exit executions (opposite direction to the trade)
      // Long trade exits = Sell/SellShort; Short trade exits = Buy/BuyToCover
      const exitExecs = execs.filter(exec => {
        const action = exec.OrderAction || exec.Action || '';
        const isBuy = action === 'Buy' || action === 'BuyToCover';
        const isSell = action === 'Sell' || action === 'SellShort';
        return direction === 'long' ? isSell : isBuy;
      });

      if (exitExecs.length === 0) {
        console.log(`[useJournifiSync] Orphaned execs for ${accountName}|${symbol} are not exits for ${direction} trade — skipping`);
        return;
      }

      console.log(`[useJournifiSync] Found open trade to complete: ${openTrade.symbol} ${openTrade.direction} (${openTrade.id}) with ${exitExecs.length} exit(s)`);

      // Build exit data from the orphaned exit executions
      // getSpec() normalizes symbol form (root/space/short-code).
      const spec = getSpec(symbol) || { tickSize: 0.25, tickValue: 0.50 };
      const avgEntryPrice = parseFloat(openTrade.entry) || 0;

      const newExits = exitExecs.map(exit => ({
        quantity: parseInt(exit.Quantity) || 0,
        price: parseFloat(exit.Price) || 0,
        time: formatEtTime(exit.Timestamp)
      }));

      // Merge with any existing exits on the trade (keep non-empty ones)
      const existingExits = (openTrade.exits || []).filter(e => e.price && e.quantity);
      const allExits = [...existingExits, ...newExits];

      // Recalculate P&L with all exits
      let totalPnL = 0;
      let totalExitCommission = 0;
      allExits.forEach(exit => {
        const qty = parseInt(exit.quantity) || 0;
        const exitPrice = parseFloat(exit.price) || 0;
        const priceDiff = direction === 'long'
          ? exitPrice - avgEntryPrice
          : avgEntryPrice - exitPrice;
        const ticks = safeTicks(priceDiff, spec.tickSize);
        totalPnL += ticks * spec.tickValue * qty;
      });
      exitExecs.forEach(exit => {
        totalExitCommission += parseFloat(exit.Commission) || 0;
      });

      // Get last exit time
      let exitTimeStr = '';
      if (allExits.length > 0) {
        exitTimeStr = allExits[allExits.length - 1].time || '';
      }

      // Check if position is now flat (all contracts exited)
      const totalEntryQty = parseInt(openTrade.quantity) || 0;
      const totalExitQty = allExits.reduce((sum, e) => sum + (parseInt(e.quantity) || 0), 0);
      const isNowComplete = totalExitQty >= totalEntryQty;

      // Build updated trade
      const existingCommission = parseFloat(openTrade.importedCommission) || 0;

      // Calculate average exit price (weighted by quantity)
      const totalExitedQty = allExits.reduce((sum, e) => sum + (parseInt(e.quantity) || 0), 0);
      const weightedExitPrice = totalExitedQty > 0
        ? allExits.reduce((sum, e) => sum + (parseFloat(e.price) || 0) * (parseInt(e.quantity) || 0), 0) / totalExitedQty
        : null;

      const updatedTrade = {
        ...openTrade,
        exit: weightedExitPrice != null ? weightedExitPrice.toString() : openTrade.exit,
        exits: allExits.length > 0 ? allExits : [{ quantity: '', price: '', time: '' }],
        exitTime: exitTimeStr,
        pnl: isNaN(totalPnL) ? 0 : parseFloat(totalPnL.toFixed(2)),
        importedCommission: existingCommission + totalExitCommission,
        isOpen: !isNowComplete,
        notes: openTrade.notes?.replace('Auto-synced from NinjaTrader', 'Auto-synced from NinjaTrader (exits completed)')
          || openTrade.notes?.replace('Imported from JournifiSync', 'Imported from JournifiSync (exits completed)')
          || openTrade.notes,
        _journifiSync: {
          ...openTrade._journifiSync,
          executionIds: [
            ...(openTrade._journifiSync?.executionIds || []),
            ...exitExecs.map(e => e.ExecutionId)
          ],
          completedAt: isNowComplete ? new Date().toISOString() : undefined
        }
      };

      // Log it
      const pnlFormatted = parseFloat(updatedTrade.pnl) >= 0
        ? `+$${parseFloat(updatedTrade.pnl).toFixed(2)}`
        : `-$${Math.abs(parseFloat(updatedTrade.pnl)).toFixed(2)}`;
      addLogEntry(
        isNowComplete ? 'success' : 'info',
        isNowComplete
          ? `Open trade completed: ${symbol} ${direction === 'long' ? 'LONG' : 'SHORT'} ${pnlFormatted}`
          : `Open trade updated with ${newExits.length} exit(s): ${symbol}`
      );

      console.log(`[useJournifiSync] Completing open trade ${openTrade.id}: ${pnlFormatted}, isOpen=${!isNowComplete}`);

      // Notify via callback
      try {
        onTradeUpdateRef.current(updatedTrade);

        // Clean up openTradeMapRef if trade is now complete
        if (isNowComplete) {
          const ntAccount = openTrade._journifiSync?.ntAccountName || '';
          const closeMapKey = `${ntAccount}|${symbol}`;
          openTradeMapRef.current.delete(closeMapKey);
        }

        // Mark these execution IDs as processed
        exitExecs.forEach(exec => processedIdsRef.current.add(exec.ExecutionId));

        // Play sound if trade is now complete
        if (isNowComplete && soundEnabledRef.current) {
          playNotificationSound(parseFloat(updatedTrade.pnl) >= 0);
        }

        setRecentTrades(prev => [updatedTrade, ...prev.filter(t => t.id !== openTrade.id)].slice(0, 10));
        setLastSyncTime(new Date());
      } catch (error) {
        console.error('[useJournifiSync] Error in onTradeUpdate callback:', error);
      }
    });
  }, [addLogEntry]);

  /**
   * Handle new executions from the watcher
   */
  const handleNewExecutions = useCallback((newExecutions) => {
    if (!Array.isArray(newExecutions) || newExecutions.length === 0) return;

    // ── Journal-side dedup (v7.5.14 stale-"In Position" bug fix) ────────────
    //
    // The main process re-sends today's full executions file on every
    // `journifi-sync-watch-start` (sync toggle, app reload, etc.). Without a
    // journal-side filter, the same ExecutionId gets appended to
    // `pendingExecutions[key]` more than once. The accumulated array then
    // feeds `processExecutionsToTrades`, which treats the duplicate as a
    // legit scale-in — doubling `quantity` on the trade. Because exits
    // arrive only once, the close-detection check `totalExitQty >=
    // totalEntryQty` permanently fails and the trade stays `isOpen: true`
    // even after NT shows position=0.
    //
    // Concrete repro from Chris's BX42973-117 case (2026-05-26):
    //   - Entry: Buy 2 at 30044.75 (ExecutionId 1207992|...)
    //   - Exits: Sell 1 at 30063.25, Sell 1 at 30078.75
    //   - Real-world: position 0, profit $105.
    //   - Bug state in trades.json: quantity=4, entries=[{qty:2},{qty:2}],
    //     executionIds=["1207992|...","1207992|...","1209322|...","1318671|..."],
    //     isOpen=true — entry ExecutionId appeared twice.
    //
    // Filter incoming execs against ExecutionIds already in journal trades
    // (open or closed). `existingTradesRef.current` is kept in sync via
    // useEffect so this snapshot is current as of the most recent render.
    const journalExecIds = new Set();
    (existingTradesRef.current || []).forEach(t => {
      const ids = t?._journifiSync?.executionIds;
      if (Array.isArray(ids)) ids.forEach(id => journalExecIds.add(id));
    });
    const filteredExecutions = filterAlreadyJournaledExecutions(newExecutions, journalExecIds);
    if (filteredExecutions.length < newExecutions.length) {
      const skipped = newExecutions.length - filteredExecutions.length;
      console.log(`[useJournifiSync] Dropped ${skipped} execution(s) already in journal trades (likely watcher re-emit after sync restart)`);
    }
    if (filteredExecutions.length === 0) return;
    newExecutions = filteredExecutions;

    // Defensive dedup chokepoint: register every incoming execId in
    // processedIdsRef BEFORE doing any work. Both callers (chokidar push +
    // 3-sec poll) already add to processedIdsRef, but doing it here too
    // means any future caller path is automatically race-safe — and any
    // async boundary that gets introduced inside this function can't be
    // exploited by a concurrent caller to re-process the same exec.
    newExecutions.forEach(exec => {
      if (exec?.ExecutionId) processedIdsRef.current.add(exec.ExecutionId);
    });
    evictOldestFromSet(
      processedIdsRef.current,
      PROCESSED_IDS_MAX_RENDERER,
      PROCESSED_IDS_TARGET_RENDERER,
      'processedIds'
    );

    console.log('[useJournifiSync] Processing', newExecutions.length, 'new executions');

    // Log executions received - Support both old (Instrument/InstrumentType) and new (InstrumentName) field names
    const symbols = [...new Set(newExecutions.map(e => e.InstrumentType || (e.InstrumentName || e.Instrument || '').replace(/[HMUZ]\d{1,2}$/, '')))];
    addLogEntry('info', `Received ${newExecutions.length} execution(s): ${symbols.join(', ')}`);

    // Detect NT account names that don't match any journal account or existing mapping
    // (catches prop-firm account resets — e.g., Bulenox BX-M* and Take Profit Trader
    //  both issue new account numbers on reset)
    const normalize = (s) => (s || '').replace(/[-_\s]/g, '').toLowerCase();
    const ntAccountsSeen = [...new Set(newExecutions.map(e => e.AccountName).filter(Boolean))];
    ntAccountsSeen.forEach(ntName => {
      const mapping = accountMappingsRef.current?.[ntName];
      if (mapping) {
        // Explicitly mapped — but warn if destination is not Active (common foot-gun after a prop-firm reset)
        const destAccount = accountsRef.current?.find(a => a.id === mapping);
        if (destAccount && destAccount.status && destAccount.status !== 'Active') {
          // Only log once per session per (ntName, mapping) pair — not on every execution
          const warnKey = `nt-map-inactive:${ntName}:${mapping}`;
          if (!inactiveMappingWarnedRef.current.has(warnKey)) {
            inactiveMappingWarnedRef.current.add(warnKey);
            addLogEntry(
              'warning',
              `"${ntName}" is mapped to "${destAccount.name || destAccount.id}" which is ${destAccount.status} — those trades will be hidden by the Active-only filter. Remap or reactivate.`
            );
          }
        }
        return;
      }
      const normNT = normalize(ntName);
      const autoMatch = accountsRef.current?.find(a =>
        normalize(a.id) === normNT || normalize(a.name) === normNT
      );
      if (autoMatch) return;

      // Not mapped, not auto-matched — try auto-create via callback before flagging
      if (typeof onNewAccountDetected === 'function') {
        // Guard: don't call the callback twice for the same NT name in the same session
        if (!autoCreatedRef.current.has(ntName)) {
          autoCreatedRef.current.add(ntName);
          try {
            const newAccountId = onNewAccountDetected(ntName);
            if (newAccountId) {
              // Update refs SYNCHRONOUSLY so the CURRENT batch of trades routes correctly.
              // setAccountMapping / addAccount are async React state — the ref updates below
              // ensure processExecutionsToTrades (called immediately after this loop) sees
              // the new mapping + account without waiting for the next render cycle.
              accountMappingsRef.current = { ...(accountMappingsRef.current || {}), [ntName]: newAccountId };
              if (Array.isArray(accountsRef.current) && !accountsRef.current.some(a => a.id === newAccountId)) {
                // Best-effort: append a lightweight placeholder so fuzzy fallbacks resolve.
                // The parent handler (onNewAccountDetected) owns the real account creation.
                accountsRef.current = [
                  ...accountsRef.current,
                  { id: newAccountId, name: newAccountId, status: 'Active' }
                ];
              }
              addLogEntry(
                'info',
                `Auto-created journal account "${newAccountId}" for new NT account "${ntName}". Review balance/commission in Accounts Manager if needed.`
              );
              // Persist the mapping so future executions route cleanly (async, for React re-render + settings.json)
              setAccountMapping(ntName, newAccountId);
              return;
            }
          } catch (err) {
            addLogEntry('warning', `Auto-create failed for "${ntName}": ${err?.message || err}`);
          }
        } else {
          // Already attempted auto-create this session but it returned falsy — fall through to flag
        }
      }

      // Fallback: flag as unmapped + log warning
      setUnmappedAccounts(prev => prev.includes(ntName) ? prev : [...prev, ntName]);
      if (!Object.prototype.hasOwnProperty.call(accountMappingsRef.current || {}, ntName)) {
        addLogEntry(
          'warning',
          `New NT account "${ntName}" detected — no journal account match. If this is a prop-firm reset (Bulenox, Take Profit Trader, etc.), map it in Account Mapping.`
        );
      }
    });

    // Group executions by account + instrument + date
    const groups = {};
    newExecutions.forEach(exec => {
      const symbol = exec.InstrumentType || (exec.InstrumentName || exec.Instrument || '').replace(/[HMUZ]\d{1,2}$/, '');
      const date = exec.Timestamp.split('T')[0];
      const key = `${exec.AccountName}|${symbol}|${date}`;

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(exec);
    });

    // Collect trades to report OUTSIDE of setState (to avoid React batching issues)
    const tradesToReport = [];  // New trades → onTradeComplete
    const tradesToUpdate = [];  // Updates to existing open trades → onTradeUpdate
    const positionUpdates = [];
    const keysToDelete = [];
    const orphanedExitExecs = []; // Exits that couldn't form complete trades — may belong to open journal trades

    // Compute the new pendingExecutions in the function body (Strict-Mode-safe).
    // The previous implementation ran this inside a setPendingExecutions(prev => ...)
    // updater, but that updater ALSO mutated outer arrays + refs (tradesToReport,
    // openTradeMapRef, reportedTradeIdsRef, etc.). React 18 Strict Mode invokes
    // updaters twice in dev, which double-emitted trades and corrupted refs.
    // We now read from pendingExecutionsRef (kept in sync via useEffect) and
    // commit the result with a single setPendingExecutions(updated) call below.
    {
      const updated = { ...pendingExecutionsRef.current };

      Object.entries(groups).forEach(([key, execs]) => {
        if (!updated[key]) {
          updated[key] = [];
        }
        // Belt-and-suspenders: dedup the per-key accumulator by ExecutionId.
        // The top-level journal filter above catches the common case (watcher
        // re-emit after sync restart), but a same-session race could in
        // principle add the same exec to the queue twice between renders.
        // `dedupExecutionsByExecutionId` keeps the FIRST occurrence — combined
        // with appending `execs` AFTER `updated[key]`, this means a previously-
        // seen ExecutionId wins over a re-emitted copy.
        updated[key] = dedupExecutionsByExecutionId([...updated[key], ...execs]);

        // Process all executions for this key - may produce multiple trades
        const allExecs = updated[key];
        allExecs.sort((a, b) => {
          const timeDiff = new Date(a.Timestamp) - new Date(b.Timestamp);
          if (timeDiff !== 0) return timeDiff;
          const aSeq = parseInt(a.ExecutionId?.split('|')[0]) || 0;
          const bSeq = parseInt(b.ExecutionId?.split('|')[0]) || 0;
          return aSeq - bSeq;
        });

        // Calculate current position
        let position = 0;
        allExecs.forEach(exec => {
          // Support both field name formats for backwards compatibility
          const action = exec.OrderAction || exec.Action || '';
          const isBuy = action === 'Buy' || action === 'BuyToCover';
          const qty = parseInt(exec.Quantity) || 0;
          position += isBuy ? qty : -qty;
        });

        // Collect position update for later
        const [accountName, symbol] = key.split('|');
        positionUpdates.push({ accountName, symbol, position, executions: allExecs });

        // Use new multi-trade processor to get all completed trades
        const trades = processExecutionsToTrades(allExecs, {
          existingAccounts: accountsRef.current,
          accountMappings: accountMappingsRef.current,
          source: 'auto-sync'
        });

        // Separate completed (closed) and open (in-progress) trades
        const completedTrades = trades.filter(t => !t.isOpen);
        const openTrades = trades.filter(t => t.isOpen);

        // --- OPEN TRADE ROUTING (Real-Time In-Progress Journaling) ---
        openTrades.forEach(trade => {
          const mapKey = `${trade._journifiSync?.ntAccountName || accountName}|${trade.symbol}`;
          const openEntry = openTradeMapRef.current.get(mapKey);

          if (!openEntry) {
            // NEW open trade — first entry for this position
            openTradeMapRef.current.set(mapKey, {
              tradeId: trade.id,
              execIds: new Set(trade._journifiSync?.executionIds || [])
            });
            // Account-aware signature — shared with manual import + CSV for cross-method continuity.
            const tradeSig = buildTradeSignature(trade);
            if (!reportedTradeIdsRef.current.has(tradeSig)) {
              reportedTradeIdsRef.current.add(tradeSig);
              tradesToReport.push(trade); // Goes via onTradeComplete — creates journal entry
            }
          } else {
            // EXISTING open trade — scale-in or position update
            // Update stored exec IDs to include new ones (scale-ins, partial exits)
            (trade._journifiSync?.executionIds || []).forEach(id => openEntry.execIds.add(id));
            const updateTrade = { ...trade, id: openEntry.tradeId };
            tradesToUpdate.push(updateTrade); // Goes via onTradeUpdate — preserves user edits
          }
        });

        // --- COMPLETED TRADE ROUTING ---
        completedTrades.forEach(trade => {
          const mapKey = `${trade._journifiSync?.ntAccountName || accountName}|${trade.symbol}`;
          const openEntry = openTradeMapRef.current.get(mapKey);

          if (openEntry) {
            // Verify this completed trade actually belongs to the tracked open position
            // by checking for overlapping execution IDs (prevents collision when multiple
            // sequential trades for same symbol coexist in pendingExecutions)
            const tradeExecIds = trade._journifiSync?.executionIds || [];
            const hasOverlap = tradeExecIds.some(id => openEntry.execIds.has(id));

            if (hasOverlap) {
              // Trade was open in journal, now completed — route via onTradeUpdate
              openTradeMapRef.current.delete(mapKey);
              const updateTrade = { ...trade, id: openEntry.tradeId };
              tradesToUpdate.push(updateTrade);
            } else {
              // Different position for same symbol — route as new completed trade
              const tradeSig = buildTradeSignature(trade);
              if (!reportedTradeIdsRef.current.has(tradeSig)) {
                reportedTradeIdsRef.current.add(tradeSig);
                tradesToReport.push(trade);
              }
            }
          } else {
            // Standard completed trade (was never open in journal, or instant scalp)
            const tradeSig = buildTradeSignature(trade);

            if (!reportedTradeIdsRef.current.has(tradeSig)) {
              reportedTradeIdsRef.current.add(tradeSig);
              tradesToReport.push(trade);
            }
          }
        });

        // --- ORPHANED EXITS (exits for trades whose entries are already in journal) ---
        // Only triggers when we got no completed trades AND no new open trades from this batch
        if (completedTrades.length === 0 && openTrades.length > 0) {
          const mapKey = `${openTrades[0]._journifiSync?.ntAccountName || accountName}|${openTrades[0].symbol}`;
          if (openTradeMapRef.current.has(mapKey)) {
            // Already tracked — handled above as scale-in/update
          } else {
            // These open trades couldn't be matched — likely orphaned exits
            const newExecIds = new Set(execs.map(e => e.ExecutionId));
            const newOrphanExecs = allExecs.filter(e => newExecIds.has(e.ExecutionId));
            if (newOrphanExecs.length > 0) {
              orphanedExitExecs.push(...newOrphanExecs);
            }
          }
        }

        // Mark keys to delete if position is flat
        if (position === 0 && completedTrades.length > 0) {
          keysToDelete.push(key);
        } else if (completedTrades.length > 0) {
          // Position is NOT flat (still open) but we did flush some completed
          // trades — prune their settled execIds out of the pending list so
          // the next batch doesn't re-process them.
          //
          // Without this, a position that goes 5 long → flat → 3 long → flat → 7 long
          // would carry all 30+ exec records forward each batch (re-grouping,
          // re-sorting, re-classifying), turning processing into O(n²) over a
          // long session for an active scalper.
          //
          // The flat-flush case (above) deletes the entire key — already O(1).
          // This branch handles the "open after flush" case.
          const settledExecIds = new Set();
          for (const t of completedTrades) {
            const ids = t._journifiSync?.executionIds;
            if (Array.isArray(ids)) ids.forEach(id => settledExecIds.add(id));
          }
          if (settledExecIds.size > 0) {
            updated[key] = updated[key].filter(e => !settledExecIds.has(e.ExecutionId));
          }
        }
      });

      // Clean up completed groups
      keysToDelete.forEach(key => delete updated[key]);

      // Commit the new pendingExecutions in a single, side-effect-free call.
      // Update the ref synchronously so a second handleNewExecutions firing in
      // the same tick (rare, but possible) sees the latest accumulator instead
      // of the stale React state.
      pendingExecutionsRef.current = updated;
      setPendingExecutions(updated);
    }

    // AFTER setState: Execute side effects (callbacks, sounds, state updates)
    // Use setTimeout to ensure this runs after React's state batch completes
    setTimeout(() => {
      // Notify position updates
      positionUpdates.forEach(update => {
        if (onPositionUpdateRef.current) {
          onPositionUpdateRef.current(update);
        }
      });

      // Try to complete open journal trades with orphaned exit executions
      if (orphanedExitExecs.length > 0) {
        console.log(`[useJournifiSync] ${orphanedExitExecs.length} orphaned exit(s) — checking for matching open journal trades`);
        tryCompleteOpenTrades(orphanedExitExecs);
      }

      // Process new trades (both completed and NEW open/in-progress)
      if (tradesToReport.length > 0) {
        const openCount = tradesToReport.filter(t => t.isOpen).length;
        const closedCount = tradesToReport.length - openCount;
        console.log(`[useJournifiSync] Reporting ${tradesToReport.length} trade(s) (${closedCount} closed, ${openCount} live)`);

        tradesToReport.forEach((trade, index) => {
          if (trade.isOpen) {
            // LIVE trade — just entered position
            console.log('[useJournifiSync] Trade opened (LIVE):', trade.symbol, trade.direction);
            addLogEntry(
              'info',
              `Trade opened: ${trade.symbol} ${trade.direction === 'long' ? 'LONG' : 'SHORT'} — journaling in progress`
            );
          } else {
            // Completed trade — position closed
            console.log('[useJournifiSync] Trade complete:', trade.symbol, trade.direction, trade.pnl);
            const pnlFormatted = parseFloat(trade.pnl) >= 0
              ? `+$${parseFloat(trade.pnl).toFixed(2)}`
              : `-$${Math.abs(parseFloat(trade.pnl)).toFixed(2)}`;
            addLogEntry(
              parseFloat(trade.pnl) >= 0 ? 'success' : 'warning',
              `Trade closed: ${trade.symbol} ${trade.direction === 'long' ? 'LONG' : 'SHORT'} ${pnlFormatted}`
            );
          }

          // Warn if account matching failed
          if (!trade.accountId && trade._journifiSync?.ntAccountName) {
            const ntName = trade._journifiSync.ntAccountName;
            addLogEntry(
              'warning',
              `No account match for NT account "${ntName}" — set up a mapping in Account Mapping`
            );
            // Track as unmapped for UI surfacing (e.g., prop-firm reset accounts — Bulenox, TPT, etc.)
            setUnmappedAccounts(prev => prev.includes(ntName) ? prev : [...prev, ntName]);
          }

          // Play notification sound (only for completed trades, last one to avoid spam)
          if (!trade.isOpen && soundEnabledRef.current && index === tradesToReport.length - 1) {
            const hasWin = tradesToReport.filter(t => !t.isOpen).some(t => parseFloat(t.pnl) >= 0);
            playNotificationSound(hasWin);
          }

          // Add to recent trades
          setRecentTrades(prev => [trade, ...prev].slice(0, 10));

          // Update last sync time
          setLastSyncTime(new Date());

          // Notify callback — creates journal entry
          if (onTradeCompleteRef.current) {
            try {
              console.log('[useJournifiSync] Calling onTradeComplete callback for:', trade.id, trade.isOpen ? '(LIVE)' : '(closed)');
              onTradeCompleteRef.current(trade);
            } catch (error) {
              console.error('[useJournifiSync] Error in onTradeComplete callback:', error);
            }
          } else {
            console.warn('[useJournifiSync] No onTradeComplete callback registered!');
          }
        });
      }

      // Process updates to existing open trades (scale-ins, partial exits, completions)
      if (tradesToUpdate.length > 0) {
        console.log('[useJournifiSync] Updating', tradesToUpdate.length, 'existing trade(s)');

        tradesToUpdate.forEach(trade => {
          if (!trade.isOpen) {
            // Trade was open, now completed
            console.log('[useJournifiSync] Open trade completed:', trade.symbol, trade.direction, trade.pnl);
            const pnlFormatted = parseFloat(trade.pnl) >= 0
              ? `+$${parseFloat(trade.pnl).toFixed(2)}`
              : `-$${Math.abs(parseFloat(trade.pnl)).toFixed(2)}`;
            addLogEntry(
              parseFloat(trade.pnl) >= 0 ? 'success' : 'warning',
              `Trade closed: ${trade.symbol} ${trade.direction === 'long' ? 'LONG' : 'SHORT'} ${pnlFormatted}`
            );

            // Play sound for trade completion
            if (soundEnabledRef.current) {
              playNotificationSound(parseFloat(trade.pnl) >= 0);
            }
          } else {
            // Scale-in or partial exit (still open)
            console.log('[useJournifiSync] Trade updated (still LIVE):', trade.symbol, 'qty:', trade.quantity);
            addLogEntry('info', `Position updated: ${trade.symbol} qty ${trade.quantity}`);
          }

          // Update recent trades list with latest trade data (P&L, status, etc.)
          setRecentTrades(prev => {
            const existingIdx = prev.findIndex(rt => rt.id === trade.id);
            if (existingIdx >= 0) {
              // Replace the stale entry with updated data
              const updated = [...prev];
              updated[existingIdx] = trade;
              return updated;
            } else {
              // Trade wasn't in recent list (e.g., pre-existing open trade from before sync started)
              return [trade, ...prev].slice(0, 10);
            }
          });

          // Update last sync time
          setLastSyncTime(new Date());

          // Notify callback — updates existing journal entry in-place
          if (onTradeUpdateRef.current) {
            try {
              onTradeUpdateRef.current(trade);
            } catch (error) {
              console.error('[useJournifiSync] Error in onTradeUpdate callback:', error);
            }
          }
        });
      }
    }, 0);
    // evictOldestFromSet, onNewAccountDetected, setAccountMapping are stable
    // refs read via the latest-callback pattern below, so excluding them is
    // intentional — including them would churn this callback on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLogEntry, tryCompleteOpenTrades]);

  // Ref for polling interval and processed IDs
  const pollingIntervalRef = useRef(null);
  const processedIdsRef = useRef(new Set());
  const reportedTradeIdsRef = useRef(new Set()); // Track trades already sent to onTradeComplete
  const syncFolderRef = useRef(syncFolder);
  const existingTradesRef = useRef(existingTrades); // Track existing trades for deduplication

  // Cap dedup Sets to prevent unbounded growth across long multi-day sessions.
  // Set preserves insertion order, so we evict the oldest entries when over the
  // threshold. The gap between MAX and TARGET prevents flap-eviction.
  const PROCESSED_IDS_MAX_RENDERER = 10000;
  const PROCESSED_IDS_TARGET_RENDERER = 8000;
  const evictOldestFromSet = useCallback((set, max, target, label) => {
    if (set.size <= max) return;
    const toRemove = set.size - target;
    let i = 0;
    for (const id of set) {
      if (i >= toRemove) break;
      set.delete(id);
      i++;
    }
    console.log(`[useJournifiSync] Evicted ${toRemove} oldest ${label}; set now ${set.size}`);
  }, []);
  
  // Keep syncFolder ref updated
  useEffect(() => {
    syncFolderRef.current = syncFolder;
  }, [syncFolder]);
  
  // Keep existingTrades ref updated
  useEffect(() => {
    existingTradesRef.current = existingTrades;
  }, [existingTrades]);

  // Keep openTradeMapRef in sync with journal state
  // When Import (or any external source) adds an open trade, real-time sync needs to know
  // so it routes subsequent exits through onTradeUpdate instead of creating a duplicate.
  // Also registers imported CLOSED-trade exec IDs in processedIdsRef to prevent re-processing.
  // IMPORTANT: We intentionally SKIP open trade exec IDs here (same as startSync) so that
  // their entries remain in the processing pipeline for FIFO context on subsequent exits.
  useEffect(() => {
    if (!status.watching) return;

    existingTrades.forEach(trade => {
      if (!trade._journifiSync) return;
      const execIds = trade._journifiSync.executionIds || [];

      if (trade.isOpen) {
        // Register open trades in the map so exits route to onTradeUpdate
        const ntAccount = trade._journifiSync.ntAccountName || '';
        const mapKey = `${ntAccount}|${trade.symbol}`;
        const existing = openTradeMapRef.current.get(mapKey);
        if (!existing || existing.tradeId !== trade.id) {
          openTradeMapRef.current.set(mapKey, {
            tradeId: trade.id,
            execIds: new Set(execIds)
          });
        } else {
          // Same trade — just ensure all exec IDs are tracked (may have grown via scale-in)
          execIds.forEach(id => existing.execIds.add(id));
        }
        // DO NOT add open trade exec IDs to processedIdsRef — they need FIFO context
      } else {
        // Only mark CLOSED trade exec IDs as processed (prevents re-ingestion from Import)
        execIds.forEach(id => processedIdsRef.current.add(id));
      }
    });
  }, [existingTrades, status.watching]);

  /**
   * Poll for executions (backup to file watcher)
   */
  const pollForExecutions = useCallback(async () => {
    if (!isElectron()) return;
    
    try {
      // Get list of files
      const filesResult = await window.electronAPI.journifiSync.listFiles(syncFolderRef.current);
      
      if (!filesResult.success || !filesResult.files || filesResult.files.length === 0) return;
      
      // Read today's file (most recent trades). Files are named by trading
      // day (ET-anchored) — match against getCurrentTradingDay so the lookup
      // works on non-ET hosts and during the 5:15pm prep window.
      const today = getCurrentTradingDay();
      const todayFile = filesResult.files.find(f => f.date === today);
      
      if (!todayFile) return;
      
      const fileResult = await window.electronAPI.journifiSync.readFile(todayFile.path);
      
      if (!fileResult.success || !fileResult.data || fileResult.data.length === 0) return;
      
      // Find new executions not yet processed
      const newExecutions = fileResult.data.filter(exec => !processedIdsRef.current.has(exec.ExecutionId));
      
      if (newExecutions.length > 0) {
        console.log('[useJournifiSync] Polling found', newExecutions.length, 'new executions');
        
        // Mark as processed
        newExecutions.forEach(exec => processedIdsRef.current.add(exec.ExecutionId));
        
        // Process them
        handleNewExecutions(newExecutions);
      }
    } catch (error) {
      console.error('[useJournifiSync] Polling error:', error);
    }
  }, [handleNewExecutions]);

  /**
   * Start watching for new executions
   */
  const start = useCallback(async () => {
    if (!isElectron()) {
      setStatus({ watching: false, error: 'Requires Electron environment' });
      addLogEntry('error', 'Cannot start: Requires Electron environment');
      return false;
    }

    addLogEntry('info', 'Starting real-time sync...');
    
    // Clear processed IDs when starting fresh
    processedIdsRef.current = new Set();
    
    // Load execution IDs from ACTUAL JOURNAL TRADES so we don't re-import them.
    // The main process now sends ALL of today's executions on watcher start,
    // so the renderer is responsible for deduplication against existing journal data.
    //
    // IMPORTANT: We intentionally SKIP exec IDs from OPEN trades. Those entries need
    // to flow through handleNewExecutions so they accumulate in pendingExecutions,
    // providing FIFO context for any subsequent scale-ins or exits. Without this,
    // new execs for an open position are processed WITHOUT the original entries,
    // causing incorrect FIFO grouping (wrong trade direction/qty/P&L).
    try {
      const journalExecutionIds = new Set();
      let openTradeExecCount = 0;
      existingTradesRef.current.forEach(trade => {
        if (trade._journifiSync && trade._journifiSync.executionIds) {
          if (trade.isOpen) {
            // DON'T mark open trade exec IDs as processed — they need FIFO context
            openTradeExecCount += trade._journifiSync.executionIds.length;
          } else {
            trade._journifiSync.executionIds.forEach(id => journalExecutionIds.add(id));
          }
        }
      });

      if (journalExecutionIds.size > 0) {
        journalExecutionIds.forEach(id => processedIdsRef.current.add(id));
        evictOldestFromSet(
          processedIdsRef.current,
          PROCESSED_IDS_MAX_RENDERER,
          PROCESSED_IDS_TARGET_RENDERER,
          'processedIds (after journal pre-load)'
        );
        console.log('[useJournifiSync] Pre-loaded', journalExecutionIds.size, 'execution IDs from closed journal trades');
      }
      if (openTradeExecCount > 0) {
        console.log('[useJournifiSync] Skipped', openTradeExecCount, 'exec IDs from open trades (will re-process for FIFO context)');
      }
      if (journalExecutionIds.size === 0 && openTradeExecCount === 0) {
        console.log('[useJournifiSync] No existing journal trades — all executions will be processed as new');
      }
    } catch (e) {
      console.warn('[useJournifiSync] Could not pre-load execution IDs from journal:', e);
    }

    // Initialize open trade map from existing open journal trades
    // This prevents creating duplicate entries after app restart
    // Stores { tradeId, execIds } so we can validate overlap when routing completed trades
    openTradeMapRef.current.clear();
    try {
      const ghostTrades = []; // Open trades with no/broken accountId routing
      existingTradesRef.current.forEach(trade => {
        if (trade.isOpen && trade._journifiSync) {
          const ntAccount = trade._journifiSync.ntAccountName || '';
          const mapKey = `${ntAccount}|${trade.symbol}`;
          openTradeMapRef.current.set(mapKey, {
            tradeId: trade.id,
            execIds: new Set(trade._journifiSync.executionIds || [])
          });

          // Ghost-trade detection: open sync-sourced trade without an accountId.
          // These were likely saved during the auto-create timing bug window.
          // Next execution update will repair accountId in-place via onTradeUpdate.
          if (!trade.accountId && ntAccount) {
            ghostTrades.push({ tradeId: trade.id, ntAccount, symbol: trade.symbol });
          }
        }
      });
      if (openTradeMapRef.current.size > 0) {
        console.log('[useJournifiSync] Restored', openTradeMapRef.current.size, 'open trade(s) from journal');
      }
      if (ghostTrades.length > 0) {
        console.warn('[useJournifiSync] Detected', ghostTrades.length, 'ghost trade(s) with no accountId:', ghostTrades);
        addLogEntry(
          'warning',
          `${ghostTrades.length} open trade(s) have no account routing (${ghostTrades.map(g => `${g.ntAccount}/${g.symbol}`).join(', ')}). They will be repaired on the next execution update.`
        );
      }
    } catch (e) {
      console.warn('[useJournifiSync] Could not restore open trades from journal:', e);
    }

    const success = await startWatching({
      onNewExecutions: (execs) => {
        // Filter out already processed (in journal) and mark new ones
        const newExecs = execs.filter(exec => !processedIdsRef.current.has(exec.ExecutionId));
        if (execs.length > 0) {
          console.log(`[useJournifiSync] Received ${execs.length} from main, ${newExecs.length} are new (${execs.length - newExecs.length} already in journal)`);
        }
        newExecs.forEach(exec => processedIdsRef.current.add(exec.ExecutionId));
        if (newExecs.length > 0) {
          handleNewExecutions(newExecs);
        }
      },
      onError: (error) => {
        setStatus(prev => ({ ...prev, error }));
        addLogEntry('error', `Error: ${error}`);
      },
      onStatusChange: (newStatus) => setStatus(prev => ({ ...prev, ...newStatus }))
    });

    setIsActive(success);
    if (success) {
      addLogEntry('success', 'Real-time sync started - Watching for trades');
      
      // Start 3-second polling for near-instant sync
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(pollForExecutions, 3000);
      console.log('[useJournifiSync] 3-second polling started');
    } else {
      addLogEntry('error', 'Failed to start real-time sync');
    }
    return success;
    // evictOldestFromSet read via the latest-callback ref pattern; including
    // it would churn this callback on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNewExecutions, addLogEntry, pollForExecutions]);

  /**
   * Stop watching
   */
  const stop = useCallback(async () => {
    // Stop polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('[useJournifiSync] Polling stopped');
    }
    
    await stopWatching();
    setIsActive(false);
    setStatus({ watching: false, error: null });
    addLogEntry('info', 'Real-time sync stopped');
  }, [addLogEntry]);

  /**
   * Toggle watching
   */
  const toggle = useCallback(() => {
    if (isActive) {
      stop();
    } else {
      start();
    }
  }, [isActive, start, stop]);

  // Auto-start if enabled (from saved preference or prop)
  useEffect(() => {
    if ((autoStartEnabled || autoStart) && isElectron()) {
      start();
    }
    
    // Cleanup on unmount
    return () => {
      stop();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 💓 Listen for NinjaTrader connection status changes
  useEffect(() => {
    if (!isElectron() || !window.electronAPI?.journifiSync?.onNinjaTraderStatus) {
      return;
    }
    
    const cleanup = window.electronAPI.journifiSync.onNinjaTraderStatus((data) => {
      console.log('[useJournifiSync] NinjaTrader status:', data.connected ? '🟢 Connected' : '⚪ Disconnected');
      setNinjatraderConnected(data.connected);
      
      if (data.connected) {
        addLogEntry('success', 'NinjaTrader connected');
      } else {
        addLogEntry('warning', 'NinjaTrader disconnected');
      }
    });
    
    return cleanup;
  }, [addLogEntry]);

  // Clear log
  const clearLog = useCallback(() => {
    setSyncLog([]);
  }, []);

  return {
    isActive,
    status,
    pendingExecutions,
    recentTrades,
    syncLog,
    autoStartEnabled,
    setAutoStartEnabled,
    lastSyncTime,
    syncFolder,
    setSyncFolder,
    soundEnabled,
    setSoundEnabled,
    accountMappings,
    setAccountMapping,
    clearAccountMappings,
    unmappedAccounts,          // NT account names seen but not matched to any journal account
    dismissUnmappedAccount,    // Dismiss an unmapped account without creating a mapping
    ninjatraderConnected,  // 💓 NinjaTrader connection status
    start,
    stop,
    toggle,
    clearLog,
    isElectron: isElectron()
  };
};

export default useJournifiSync;
